/**
 * Data templates for alerts used within the app.  Dynamic values can be used
 * by using ${i}, where i corresponds to the index of the value that is passed
 * to the alert function in the alert service.
 */

import { AlertOptions } from "@ionic/angular";
import { cloneDeep } from "lodash-es";

// Define the structure of each alert
/* interface AlertData {
  id?: string;
  buttons?: {
    text: string;
    role?: string;
    handler?: (value?: any) => boolean | void;
  }[];
  header: string;
  message?: string;
  inputs?: AlertInput[];
  image?: string;
  map?: {
    [key: string]: string; // or use fixed keys if known (e.g., mass, composition, isomer)
  };
  cssClass?: string;
} */

// Define the types for the ALERTS object
export interface AlertData extends Partial<AlertOptions> {
  id?: string;
  map?: Record<string, string>;
  image?: string;
}
type Alerts = Record<string, AlertData>; // Allows for dynamic keys (though currently unused)

// Define allowed alert IDs and the template data for each
const AlertTemplates = {
  confirm_sync: {
    header: "Sync Progress",
    message: `Your progress on this device is out of sync with the cloud data.
      Would you like to update your progress from the cloud?`,
  },
  confirm_update: {
    header: "New Update Available",
    message: `An updated version of the app is available. Reload to update now?`,
  },
  confirm_assigned_remove: {
    header: "Are You Sure?",
    message: `This functional group has already been added to your build.
      Reassigning this functional group will remove it from the build along
      with any groups attached to it.`,
  },
  confirm_parent_remove: {
    header: "Are You Sure?",
    message: `This functional group has other groups attached to it. Removing
      this group will remove them as well. Are you sure you wish to proceed?`,
  },
  sample_locked: {
    header: "${0} is locked",
    message: `This sample is currently locked. You must complete some of
      the other samples to unlock this one.`,
  },
  sample_in_dev: {
    header: "${0} is locked",
    message: `This sample is currently locked as it is still under development. 
      Please check back later.`,
  },
  sample_locked_access: {
    header: "${0} is locked",
    message:
      "Your account does not have access to this sample as it is not " +
      "part of a participating educational institution. Would you like to " +
      "purchase access to all samples for ${1}?",
  },
  sign_out: {
    header: "Are you sure?",
    message: `You will be signed out of the current account.`,
  },
  reset_data: {
    header: "Are you sure?",
    message: `The progress you have made for all of the samples will be
      reset.`,
  },
  reset_password: {
    header: "Reset your password",
    message: "Enter your email address.",
    inputs: [{ type: "email", name: "email", placeholder: "Email" }],
  },
  reset_error: {
    header: "Error",
    message: "There was an error sending your password reset email. ${0}",
  },
  reset_success: {
    header: "Success",
    message:
      "Instructions on how to reset your password have been " +
      "sent to your account's e-mail address. " +
      "If the message does not appear promptly, remember to " +
      "check the 'Spam' or 'Bulk' folders.",
  },
  invalid_login: {
    header: "Login Failed",
    message: "Invalid login credentials. Please try again.",
  },
  login_failed: {
    header: "Login Failed",
    message: "Incorrect email or password. Please try again.",
  },
  molecule_too_big: {
    header: "Molecule Too Big!",
    message: `Your molecule has reached its maximum allowed size. You will not
      be able to add any more items.`,
  },
  incorrect_peak: {
    header: "Incorrect",
    map: {
      hydrogens: "The assigned number of hydrogens is incorrect.",
      splitting: `The assigned splitting is incorrect. Review the number of
        peaks within the range.`,
      assigned: `The value assigned from the reference table is incorrect. Make
        sure the values for this peak are within the range listed in the table.`,
    },
  },
  correct_peak: {
    header: "Correct",
    message: "You have correctly assigned the values to this peak.",
  },
  correct_build: {
    header: "${0}",
    message:
      "Congratulations, you have identified ${0} in the ${1} " + "sample! ${2}",
    image: "./assets/img/samples/${3}.png",
  },
  incorrect_build: {
    header: "Incorrect",
    message: "The molecule you've built is not correct. ",
    map: {
      mass: `The mass does not match the target mass for the molecule. `,
      composition: `The molecule doesn't have the correct composition.`,
      isomer: `The molecule has the correct composition, but the
        the structure is incorrect.`,
    },
  },
  no_feedback: {
    header: "Blank Feedback",
    message: "You must enter a message along with your feedback.",
  },
  feedback_failed_app_offline: {
    header: "App Offline",
    message: `You must have a working data connection or be connected to WiFi
      in order to submit feedback`,
  },
  feedback_sent: {
    header: "Feedback Sent",
    message: `Thank you for sending your feedback to us.`,
  },
  build_already_complete: {
    header: "Build Already Complete",
    message: `You cannot change your build once you've already created the
      correct answer.`,
  },
  update_error: {
    header: "Update Failed",
    message: "Something went wrong with the update process: ${0}",
  },
  update_not_found: {
    header: "Already Up To Date",
    message: "This is the most recent version of the app.",
  },
  registration: {
    header: "Register",
    message: `Register an account`,
    inputs: [
      {
        name: "email",
        placeholder: "Email",
      },
      {
        name: "password",
        placeholder: "Password",
        type: "password",
      },
    ],
  },
  registration_successful: {
    header: `Registration Successful`,
    message: `Account created. Thank you for registering.`,
  },
  registration_failed: {
    header: `Registration Failed`,
    message: "There was a problem creating your account. ${0}",
  },
  registration_invalid_email: {
    header: `Registration Failed`,
    message: `Registration is currently only available to a closed audience.`,
  },
  registration_invalid_input: {
    header: `Registration Failed`,
    message: `Please provide both a username and a password.`,
  },
  account_limited: {
    header: "Access Limited",
    message: `Your account does not have access to all samples as it is not
      part of a participating educational institution.`,
  },
  internet_required: {
    header: "Internet Access Required",
    message: `Getting a completion certificate requires an internet 
      connection. Please connect to wifi or cellular data then try again.`,
  },
  no_purchases: {
    header: "No Purchases Made",
    message: "No purchases have been made on this device to restore.",
  },
  restore_confirmed: {
    header: "Purchases Restored",
    message: "Purchases have been successfully restored for this device.",
  },
  unnecessary_restore: {
    header: "Purchases Already Available",
    message: "Purchases are already available for this device.",
  },
  finished_purchase: {
    header: "Thank You",
    message: "All samples are now available.",
  },
  service_unavailable: {
    header: "Purchase Failed",
    message: "Purchase did not complete due to network connection issue.",
  },
  billing_unavailable: {
    header: "Billing Unavailable",
    message: "Billing service unavailable on this device.",
  },
  product_unavailable: {
    header: "Purchase Unavailable",
    message: "Unlocking all samples cannot be purchased at this time.",
  },
  developer_error: {
    header: "Developer Error",
    message: `An error has occurred due to developer error. Please let us know 
    if this problem has occured by sending us feedback.`,
  },
  result_error: {
    header: "Fatal Error",
    message:
      "An error has occured that has cancelled the transaction. Please try again.",
  },
  item_already_owned: {
    header: "Samples Already Accessible",
    message: `Samples are already available; purchase cancelled. 
    If you have purchased all samples on another device, restore the purchase from the dropdown menu.`,
  },
  item_not_owned: {
    header: "All Samples Not Unlocked",
    message:
      "All samples have not been unlocked. Please purchase all samples to use this purchase.",
  },
  purchase_error: {
    header: "An Error Occured During Purchase",
    message: "${0}",
  },
  store_error: {
    header: "Store Error Has Occured",
    message: "${0}",
  },
  purchase_cancelled: {
    header: "Purchase Cancelled",
    message: "Please try again.",
  },
  reauth_failed: {
    header: "Validation Failed",
    message: "We were unable to confirm your identity.\n${0}",
  },
  account_delete_success: {
    header: "Account Deleted",
    message: "Your account has been deleted and you have been signed out.",
  },
  account_delete_failed: {
    header: "Account Delete Failed",
    message: "${0}",
  },
  account_delete_offline: {
    header: "Error: Disconnected",
    message: "Ensure you are connected to the internet and try again.",
  },
  about: {
    header: "Mastering Spectroscopy",
    message: "${0}",
    cssClass: "alert-about",
  },
  pdf_generation_failed: {
    header: "Error Generating Certificate PDF",
    message: "${0}",
  },
  pdf_click_to_open: {
    header: "Certificate PDF Ready",
    message: "Click OK to open.",
  },
};

/** Export defined alerts */
export const ALERTS = AlertTemplates as Alerts;

/** Alert IDs defined in {@link AlertTemplates} */
export type AlertId = keyof typeof AlertTemplates;

/** {@link AlertId} type predicate */
export function isAlertId(id: string): id is AlertId {
  return id in AlertTemplates;
}

/**
 * An object with `.id` property value enumerated in {@link AlertTemplates}
 */
export type WithDefinedId<T extends { id?: string }> = T & {
  id: AlertId;
};

/**
 * Validate the `.id` of an alert-related struct (e.g. {@link AlertOptions}, {@link AlertData})
 */
export function hasDefinedId<
  T extends {
    id?: string;
  },
>(x: T): x is WithDefinedId<T> {
  return x !== null && x.id !== undefined && x.id in ALERTS;
}

/**
 * Prepare an AlertData based on the template for the specified ID.
 *
 * @throws {Error} if `id` is not defined in {@link AlertTemplates}.
 *
 **/
export function alertTemplateForId(id: AlertId): WithDefinedId<AlertData> {
  if (!isAlertId(id)) {
    throw Error("no alert exists with id: " + id);
  }

  const alertData = cloneDeep(ALERTS[id]);
  alertData.id = id;
  return alertData as WithDefinedId<AlertData>;
}
